import { PlusIcon } from "lucide-react";
import { z } from "zod";
import { SubmitHandler, useForm } from "react-hook-form";
import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog/alert-dialog";
import { Button } from "@/components/ui/button/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form/form";
import { Input } from "@/components/ui/input/input";
import { createNewLoanRequest } from "@/config/mutation/employee";
import { toast } from "sonner";
import { AxiosError } from "axios";
import { UploadAttachment } from "./upload-attachement";
import { Separator } from "@/components/ui/separator/separator";

const formSchema = z.object({
  totalAmountRequested: z.coerce.number().min(0).default(0),
  attachment: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

export const RequestLoan = () => {
  const empKey = Cookies.get("key") as string;
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      totalAmountRequested: 0,
      attachment: "",
    },
  });

  const { mutate, isPending } = useMutation({
    mutationFn: createNewLoanRequest(empKey),
  });

  const onSubmit: SubmitHandler<FormValues> = (values: FormValues) => {
    // console.log(values);

    mutate(values, {
      onSuccess: async () => {
        // console.log("New Employee", data);
        await queryClient.invalidateQueries({
          queryKey: ["employee-loans"],
        });

        toast.success("New Loan Request Created!");

        setTimeout(() => {
          form.setValue("totalAmountRequested", 0);
          form.setValue("attachment", "");
        }, 1000);

        navigate("/employee/loan", {
          replace: true,
        });
        setOpen(false);
      },

      onError: (error) => {
        console.log("ERROR WHILE CREATING NEW LOAN REQUEST", error);
        const isAxiosError = error instanceof AxiosError;
        if (isAxiosError) {
          const errorReason = error?.response?.data?.error as string;
          toast.error(errorReason);
        } else {
          toast.error(error?.message);
        }
      },
    });
  };

  return (
    <div>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogTrigger className="tracking-normal">
          <Button className="flex items-center gap-1">
            <PlusIcon />
            <span>Request Loan</span>
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Request Loan</AlertDialogTitle>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)}>
                <div>
                  <FormField
                    name="totalAmountRequested"
                    control={form.control}
                    render={({ field }) => {
                      return (
                        <FormItem>
                          <FormLabel>Total Requested Amount</FormLabel>
                          <FormControl>
                            <Input
                              type="number"
                              {...field}
                              disabled={isPending}
                            />
                          </FormControl>
                        </FormItem>
                      );
                    }}
                  />

                  <Separator className="my-4 mt-8" />

                  <FormField
                    control={form.control}
                    name="attachment"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Upload Attachment</FormLabel>
                        <FormControl>
                          <UploadAttachment
                            value={field.value}
                            onChange={field.onChange}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <AlertDialogFooter className="mt-5">
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <Button type="submit" disabled={isPending}>
                    Request
                  </Button>
                </AlertDialogFooter>
              </form>
            </Form>
          </AlertDialogHeader>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
